import { ChangeDetectionStrategy, Component, OnDestroy, OnInit, effect, inject, viewChild } from '@angular/core';
import { Router } from '@angular/router';
import { IonApp, IonLoading, IonRouterOutlet, NavController } from '@ionic/angular/standalone';
import { TranslateService } from '@ngx-translate/core';
// Translation imports - organized alphabetically to minimize merge conflicts
// When adding new languages, please:
// 1. Add import statement in alphabetical order by language code
// 2. Add setTranslation call in alphabetical order by language code
// 3. Update platform configuration files (Android locales_config.xml, iOS Info.plist)
import translationsBG from '../assets/i18n/bg.json';
import translationsDE from '../assets/i18n/de.json';
import translationsEN from '../assets/i18n/en.json';
import translationsNL from '../assets/i18n/nl.json';
import translationsPL from '../assets/i18n/pl.json';
import translationsPT from '../assets/i18n/pt.json';
import translationsRO from '../assets/i18n/ro.json';
import translationsTR from '../assets/i18n/tr.json';
import { AuthenticationState } from './enums/authentication-state.enum';
import { AppService } from './services/app.service';
import { AuthenticationService } from './services/authentication.service';
import { LoadingService } from './services/loading.service';
import { routerAnimation } from './utils/router-animation.utils';

/** When enabled, fakes a 2rem safe area on all sides through the same `--safe-area-inset-*`
 * variables the Android inset listener writes, so the debug view proves the real chain.
 * The safe areas are made visible with a partially transparent purple background.
 */
const debugSafeArea = false;

@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [IonApp, IonLoading, IonRouterOutlet],
})
export class AppComponent implements OnInit, OnDestroy {
  #appService = inject(AppService);
  private readonly navController = inject(NavController);
  private readonly router = inject(Router);
  private loadingService = inject(LoadingService);
  private loadingIndicator = viewChild(IonLoading);
  private readonly authenticationService = inject(AuthenticationService);
  private readonly translate = inject(TranslateService);
  protected debugSafeArea = debugSafeArea;

  routerAnimation = routerAnimation;

  constructor() {
    const defaultLanguage = 'en';

    // Register translations - organized alphabetically to minimize merge conflicts
    // When adding new languages, add the setTranslation call in alphabetical order
    this.translate.setTranslation('bg', translationsBG);
    this.translate.setTranslation('de', translationsDE);
    this.translate.setTranslation('en', translationsEN);
    this.translate.setTranslation('nl', translationsNL);
    this.translate.setTranslation('pl', translationsPL);
    this.translate.setTranslation('pt', translationsPT);
    this.translate.setTranslation('ro', translationsRO);
    this.translate.setTranslation('tr', translationsTR);

    const language = this.translate.getBrowserLang() ?? defaultLanguage;
    this.translate.setDefaultLang(defaultLanguage);
    this.translate.use(language);
  }

  unauthenticatedEffect = effect(() => {
    const state = this.authenticationService.authenticationState();
    const currentUrl = window.location.pathname;

    if (
      state === AuthenticationState.Unauthenticated &&
      !(
        currentUrl.startsWith('/onboarding') ||
        currentUrl.startsWith('/login') ||
        currentUrl.startsWith('/reset-password') ||
        currentUrl.startsWith('/forgot-password') ||
        currentUrl.startsWith('/accept-invitation')
      )
    ) {
      this.navController.navigateRoot('/onboarding');
    }
  });

  ngOnInit(): void {
    this.#appService.initListeners();
    this.loadingService.isLoading$.subscribe((isLoading) => {
      if (isLoading) {
        this.loadingIndicator()?.present();
      } else {
        this.loadingIndicator()?.dismiss();
      }
    });

    if (this.debugSafeArea) {
      const root = document.documentElement.style;
      root.setProperty('--safe-area-inset-top', '2rem');
      root.setProperty('--safe-area-inset-right', '2rem');
      root.setProperty('--safe-area-inset-bottom', '2rem');
      root.setProperty('--safe-area-inset-left', '2rem');
    }
  }

  ngOnDestroy(): void {
    this.#appService.removeListeners();
  }
}
